import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | timeout
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("timeout"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const u = await refreshUser();
          if (!cancelled) {
            // One-off Race Pack buyers go straight to their race setup.
            if (!u?.hasAccess && u?.racePack?.purchased) {
              navigate("/race-pack", { replace: true });
              return;
            }
            setStatus("success");
          }
          return;
        }
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Payment status not ready yet, retrying:", e?.message || e); }
      if (tries.current >= 8) { if (!cancelled) setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center border border-white/10 p-10" data-testid="payment-success-page">
        <Logo className="h-12 w-12 mx-auto mb-6" />
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-[#004AAD] mx-auto mb-4" />
            <h1 className="font-display text-2xl uppercase tracking-tight">Confirming your trial…</h1>
            <p className="text-zinc-200 text-sm mt-2">One moment while we activate your access.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-[#34C759] mx-auto mb-4" />
            <h1 className="font-display text-3xl uppercase tracking-tight">You're in!</h1>
            <p className="text-zinc-200 text-sm mt-2">Your 7-day free trial is active. Build your first weekly plan now.</p>
            <Button data-testid="success-continue-btn" onClick={() => navigate("/")} className="mt-8 h-12 w-full rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase tracking-wide transition-colors">
              Go to app <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </>
        )}
        {status === "timeout" && (
          <>
            <h1 className="font-display text-2xl uppercase tracking-tight">Almost there</h1>
            <p className="text-zinc-200 text-sm mt-2">Your payment is processing. This can take a moment — try continuing to the app.</p>
            <Button data-testid="success-retry-btn" onClick={() => navigate("/")} className="mt-8 h-12 w-full rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase tracking-wide transition-colors">
              Continue <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
